/**
 * Stage S11 — `processCommands`, the **domain half**: the Bahá'í macro vocabulary.
 *
 * Ported from  (rules #17–#34 of the 138-rule `processCommands`
 * sequence) and  (rule #128). Spec: `docs/spec/00-MASTER-SPEC.md`
 * §3.6 (R-082…R-120) and §3.9 (R-158…R-175, R-269).
 *
 * ═══ THE TWO INPUT MODES — READ THIS BEFORE CHANGING ANYTHING ═══
 *
 * **Mode A (production).** `ConverterApp.tsx:112` concatenates the reference preamble ahead
 * of the document, so macro expansion (S7) runs BEFORE this stage (S11). The preamble's own
 * `\newcommand` bodies (`docs/reference/preambuleComTrad-20032026.tex:107-244`) have already
 * rewritten every domain macro into generic LaTeX — `\Nverset{1}` is `\par\smallskip
 * \leavevmode\marginpar{\scriptsize 1}` by the time it reaches here. **Every handler in this
 * file is therefore DEAD in production**, and so is every Bahá'í CSS class (RK-37 / DF-15).
 * Verse numbering survives only through `.marginpar`.
 *
 * **Mode B (empty preamble box).** The user can clear the preamble box at any time. Then no
 * macro is defined, S7 is a no-op, and these handlers DO fire — emitting `.nverset`,
 * `.appel-*`, `.text-underbar`. Same document, different HTML.
 *
 * Both modes ship. Nothing in this file may be "simplified away" because Mode A does not
 * reach it.
 *
 * ═══ DEFECTS DELIBERATELY REPRODUCED (CLAUDE.md §3, owner decision 2026-07-28) ═══
 *
 * | id    | Reproduced here                                                                |
 * |-------|--------------------------------------------------------------------------------|
 * | C-8   | Only 10 of the 12 `\appel*` variants have a handler. `\appelCR` and `\appelCB`  |
 * |       | have NONE and leak verbatim in Mode B (R-328).                                  |
 * | C-6   | `\ts` emits `<u>` in Mode A (via `\underline`, R-153) but `.text-underbar` here.|
 * | R-349 | `\Tital \Ntablet \arbsq \ltrPar \ltrNum \KAnote \KAquestion \KAreponse \glad`   |
 * |       | `\Rextrait \Nextrait \afficherComTrad \note` have no handler at all — Mode B    |
 * |       | leaks all of them verbatim and the document is unreadable.                      |
 * | R-269 | `\afficherEndNotes` yields `''` whenever no note was collected — and in Mode A   |
 * |       | the token is expanded away at S7, so this rule never runs there. Mode A's        |
 * |       | injection happens at `\theendnotes` (R-270) since the DF-1 fix, 2026-08-29.      |
 * | DF-14 | `\arbsq` has no handler here; in Mode A it degrades to `&nbsp;` (invisible).    |
 * | NEW   | `\afficherEndNotes` substitutes the notes block as a **string** replacement, so |
 * |       | `$$`, `$&`, `` $` `` and `$'` inside a translator's note are interpreted as JS   |
 * |       | replacement patterns and corrupt the rendered note (see `applyAfficherEndNotes`).|
 *
 * Purity: no I/O, no DOM, no module-level mutable state. Per-conversion state arrives in the
 * `ConversionContext`.
 */

import { extractBracedContent } from './braces'
import type { ConversionContext } from './context'
import type { DocumentMetadata } from './types'

/* ────────────────────────────────────────────────────────────────────────────────────────
 * Shared helper
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Replace `\command{…}` with `<tag class="className">…</tag>`, matching braces properly.
 *
 * Verbatim port of. Spec §3.9 helper note.
 *
 * Semantics that are load-bearing and must not be "tidied":
 * - The pattern is `` new RegExp(`\\\\${command}\\s*\\{`, 'g') `` — **case-sensitive**, and
 *   `\s*` means `\ts  {x}` and `\ts\n{x}` both match. (Contrast the `\Nverset` family at
 *   {@link applyStandaloneVerseNumbers}, which has no `\s*` — see R-171 note.)
 * - `command` is interpolated **unescaped**. Every caller passes a bare `[A-Za-z]+` name.
 * - Brace counting comes from {@link extractBracedContent}, which is nesting-aware but does
 *   **not** exempt `\{` / `\}` (see braces.ts).
 * - On an unclosed group the matched `\command{` is re-emitted verbatim and the scan
 *   continues past it — the text is preserved, not dropped.
 *
 * Exported because the generic half of S11 (R-151…R-157: `textbf`, `textit`, `underline`,
 * `texttt`, `emph`, `textsc`, `textsuperscript`) needs exactly this function. If the generic
 * module defines its own copy, the coordinator should collapse them onto this one.
 */
export function replaceCommandWithTag(
  text: string,
  command: string,
  tag: string,
  className?: string,
): string {
  // `\<command>` + optional whitespace + `{`
  const pattern = new RegExp(`\\\\${command}\\s*\\{`, 'g')
  let result = ''
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    // text before the match is copied unchanged
    result += text.substring(lastIndex, match.index)

    // the `{` is the last character of the match
    const startBrace = match.index + match[0].length - 1
    const braced = extractBracedContent(text, startBrace)

    if (braced) {
      
      const attrs = className ? ` class="${className}"` : ''
      result += `<${tag}${attrs}>${braced.content}</${tag}>`
      lastIndex = braced.endIndex + 1
      pattern.lastIndex = lastIndex
    } else {
      // brace mismatch: keep the original command text, keep scanning
      result += match[0]
      lastIndex = match.index + match[0].length
      pattern.lastIndex = lastIndex
    }
  }

  
  result += text.substring(lastIndex)
  return result
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * Rule inventory — which domain macros have a Mode-B handler and which do not
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The eleven `replaceCommandWithTag` calls at, **in source order**.
 * `[macro, cssClass]`. R-159…R-169.
 *
 * Order is preserved for fidelity; it is not observable, because no name in this list is a
 * `\s*{`-prefix of another.
 */
// PREAMBLE LINK: the twelve \appel* macros (preambuleV3.tex:314-379), each carrying its own
// CONVERTISSEUR note. This table is the MODE-B path only — with a preamble loaded, every one of
// them has already been expanded using the preamble's own definition and none of these handlers
// runs. Two entries are missing on purpose (see APPEL_WITHOUT_HANDLER below), and `appel` itself
// is not defined in the preamble at all.
// /!\ The Mode-A rendering has two defects that are NOT visible from this table and are recorded
// on the preamble lines instead: the five variants whose body wraps the argument in `{#1}` publish
// their braces as visible text (R-348), and \appelCBL / \appelCRLS lose their \Large (R-201).
// Both are fixed on the converter side, not by editing the preamble.
export const APPEL_HANDLERS: ReadonlyArray<readonly [macro: string, cssClass: string]> = [
  ['appelGI', 'appel-gi'], // R-159
  ['appelSGI', 'appel-sgi'], // R-160
  ['appelGB', 'appel-gb'], // R-161
  ['appelGR', 'appel-gr'], // R-162
  ['appelSGR', 'appel-sgr'], // R-163
  ['appelCI', 'appel-ci'], // R-164
  ['appelSCI', 'appel-sci'], // R-165
  ['appelCBL', 'appel-cbl'], // R-166
  ['appelCRS', 'appel-crs'], // R-167
  ['appelCRLS', 'appel-crls'], // R-168
  ['appel', 'appel'], // R-169,  — the base command, undefined in the preamble
] as const

/**
 * **C-8 / R-328.** The preamble defines twelve `\appel*` variants (`pre:137-170`); the
 * converter hard-codes only ten. These two have **no handler** and leak verbatim in Mode B.
 * Do not add them: that would change published output.
 */
export const APPEL_WITHOUT_HANDLER: readonly string[] = ['appelCR', 'appelCB'] as const

/**
 * **R-349.** Domain macros with no hard-coded rule anywhere in `processCommands`. They exist
 * only via preamble expansion; with an empty preamble box they leak verbatim.
 * Listed for documentation and pinned by a test — this module deliberately does nothing
 * with any of them.
 */
export const DOMAIN_MACROS_WITHOUT_HANDLER: readonly string[] = [
  'Tital', // pre:116
  'Ntablet', // pre:119
  'arbsq', // pre:173 — DF-14
  'ltrPar', // pre:177
  'ltrNum', // pre:179
  'KAnote', // pre:187
  'KAquestion', // pre:189
  'KAreponse', // pre:191
  'glad', // pre:195
  'Rextrait', // pre:200
  'Nextrait', // pre:204 — DF-2
  'afficherComTrad', // pre:241
  'note', // pre:224
  'appelCR', // pre:152 — C-8
  'appelCB', // pre:155 — C-8
] as const

/* ────────────────────────────────────────────────────────────────────────────────────────
 * R-158 (#17, ) — \ts, the transliteration overbar (*trait suscrit*, pre:183)
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * `\ts{X}` → `<span class="text-underbar">X</span>`. R-158,.
 *
 * **Mode B only.** In Mode A the preamble has already rewritten `\ts{X}` to
 * `\underline{X}` (`pre:183`), which R-153 turns into `<u>X</u>`. The two modes
 * therefore emit different markup for the same source — C-6 / DF-15.
 *
 * Case-sensitive: `\TS{th}` does **not** match here (it *does* expand in Mode A, because
 * macro expansion is case-insensitive — RK-17 / G-22).
 */
export function applyTs(html: string): string {
  return replaceCommandWithTag(html, 'ts', 'span', 'text-underbar')
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * R-159…R-169 (#18–#28, ) — the *mots d'appel*
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The ten hard-coded `\appel*` variants plus the base `\appel`, each →
 * `<p class="appel-xx">…</p>`. R-159…R-169,.
 *
 * **Mode B only.** In Mode A each expands to generic LaTeX per `pre:137-170`, and five of
 * them (`\appelGR`, `\appelSGR`, `\appelCR`, `\appelCRS`, `\appelCRLS`) leak literal braces
 * into rendered scripture because their bodies wrap the argument in `{#1}` and nothing
 * strips a non-empty group (RK-11 / R-348).
 *
 * **C-8, reproduced:** `\appelCR` and `\appelCB` are absent from the handler list, so in
 * Mode B they leak verbatim — e.g. `\appelCR{Mot d'appel centré romain}` is published as-is.
 */
export function applyAppelCommands(html: string): string {
  let result = html
  for (const [macro, cssClass] of APPEL_HANDLERS) {
    result = replaceCommandWithTag(result, macro, 'p', cssClass)
  }
  return result
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * R-170 (#29, ) — \citation / \Citation with \Nverset splitting
 * ──────────────────────────────────────────────────────────────────────────────────────── */

interface CitationPart {
  type: string
  content: string
}

/**
 * `\citation{…}` / `\Citation{…}` → a `quote` environment, **split at every `\Nverset`** so
 * each verse number lands as a *preceding sibling* of its blockquote rather than inside it.
 * That placement is the hard editorial requirement: verse numbers live in the left margin.
 * R-170,.
 *
 * **Mode B only.** In Mode A `\citation` was already rewritten to
 * `\begin{quote}…\end{quote}` (`pre:134`) and the equivalent splitting is done at S12 by
 * `processQuoteWithNverset`, which additionally emits the `quote-successive-*` classes and
 * the spurious empty leading blockquote of G-1. This handler emits neither.
 *
 * Emits *LaTeX*, not final HTML: `\begin{quote}` / `\end{quote}` are consumed at S12 and the
 * `\par\smallskip` prefix at S11 rules #96/#103. Only the `<span class="nverset">` is final.
 *
 * Behaviours that are load-bearing (all confirmed against the oracle):
 * - `/\\(?:Citation|citation)\s*\{/g` — exactly two spellings, **case-sensitive**.
 *   `\CITATION{…}` does not match in Mode B.
 * - The **first** text chunk, when it precedes any `\Nverset`, is emitted **bare** — no
 *   quote wrapper. The comment at  says this is for a leading
 *   `\appel{}`. Every later chunk is wrapped.
 * A text chunk that is **whitespace-only is dropped entirely** — the
 *   whitespace never reaches the output.
 * - The inner `\Nverset` scan uses `[^}]+`, so a nested brace truncates the verse number and
 *   spills the remainder into the following chunk.
 * - A nested `\citation` inside a `\citation` is **not** processed: `lastIndex` is advanced
 *   past the whole replacement, so the inner one leaks verbatim.
 * is an `if/else` whose two branches are identical. Collapsed here; the
 *   emitted string is unchanged.
 */
export function applyCitation(html: string): string {
  let result = html
  // R-170
  const citationRegex = /\\(?:Citation|citation)\s*\{/g
  let citationMatch: RegExpExecArray | null

  while ((citationMatch = citationRegex.exec(result)) !== null) {
    // the `{` is the last character of the match
    const braceStart = citationMatch.index + citationMatch[0].length - 1
    const braced = extractBracedContent(result, braceStart)

    if (braced) {
      const content = braced.content

      // split the body on \Nverset{…}; text chunks that are blank are dropped
      const parts: CitationPart[] = []
      // R-170/R-171,  — `[^}]+` : a nested brace truncates the verse number
      const nversetPattern = /\\Nverset\{([^}]+)\}/g
      let lastIndex = 0
      let nversetMatch: RegExpExecArray | null

      while ((nversetMatch = nversetPattern.exec(content)) !== null) {
        const textBefore = content.substring(lastIndex, nversetMatch.index)
        // whitespace-only chunks are silently discarded
        if (textBefore.trim()) {
          parts.push({ type: 'text', content: textBefore })
        }
        parts.push({ type: 'nverset', content: nversetMatch[1] ?? '' })
        lastIndex = nversetMatch.index + nversetMatch[0].length
      }
      const textAfter = content.substring(lastIndex)
      // same discard for the trailing chunk
      if (textAfter.trim()) {
        parts.push({ type: 'text', content: textAfter })
      }

      // rebuild: verse span outside, following text inside its own quote
      let replacement = ''
      let currentText = ''
      let isFirstText = true

      for (const part of parts) {
        if (part.type === 'nverset') {
          if (currentText.trim()) {
            if (isFirstText) {
              // the leading chunk is emitted with NO quote wrapper
              replacement += currentText
              isFirstText = false
            } else {
              replacement += `\\begin{quote}${currentText}\\end{quote}`
            }
            currentText = ''
          }
          // the verse number becomes a sibling of the blockquote, not a child
          replacement += `\\par\\smallskip<span class="nverset">${part.content}</span>`
          isFirstText = false
        } else {
          currentText += part.content
        }
      }
      // source has two identical branches here; the output is the same either way
      if (currentText.trim()) {
        replacement += `\\begin{quote}${currentText}\\end{quote}`
      }

      
      result =
        result.substring(0, citationMatch.index) + replacement + result.substring(braced.endIndex + 1)
      citationRegex.lastIndex = citationMatch.index + replacement.length
    } else {
      // unclosed brace: skip one character and keep scanning
      citationRegex.lastIndex = citationMatch.index + 1
    }
  }

  return result
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * R-171…R-173 (#30–#32, ) — standalone verse / paragraph / Hidden Word numbers
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * `\Nverset{n}` / `\Npar{n}` / `\Nhidden{n}` outside any `\citation`. R-171…R-173,
 *. Runs **after** {@link applyCitation}, which has already consumed the
 * `\Nverset`s inside citation bodies.
 *
 * ```
 * \Nverset{n}  →  \par\smallskip<span class="nverset">n</span>     (pre:123, \scriptsize)
 * \Npar{n}     →  \par<span class="npar">n</span>                  (pre:127, \tiny, no skip)
 * \Nhidden{n}  →  \par\bigskip<span class="nhidden">n</span>       (pre:131, \scriptsize)
 * ```
 *
 * **Mode B only.** Mode A routes all three through `\marginpar` (R-086…R-088), where
 * `\Nverset`/`\Nhidden` additionally lose their size class and keep a leading space
 * (`<span class="marginpar"> 1</span>`) while `\Npar` keeps `tiny-text`.
 *
 * Two divergences from {@link replaceCommandWithTag} that are **not** in the spec and are
 * pinned by tests:
 * - No `\s*`: `\Nverset {1}` (space before the brace) does **not** match and leaks, while
 *   `\ts {th}` does match. The two families disagree about whitespace.
 * - `[^}]+` requires a non-empty argument: `\Nverset{}` does **not** match. The `{}` is then
 *   deleted by S14/S20 (`/{}/g → ''`), publishing `\NversetTexte.` — the command name glued
 *   to the verse text.
 */
export function applyStandaloneVerseNumbers(html: string): string {
  let result = html
  // R-171
  result = result.replace(
    /\\Nverset\{([^}]+)\}/g,
    '\\par\\smallskip<span class="nverset">$1</span>',
  )
  // R-172
  result = result.replace(/\\Npar\{([^}]+)\}/g, '\\par<span class="npar">$1</span>')
  // R-173
  result = result.replace(
    /\\Nhidden\{([^}]+)\}/g,
    '\\par\\bigskip<span class="nhidden">$1</span>',
  )
  return result
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * R-174 (#33, ) — \afficherTitre{}
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Build the title block for `\afficherTitre{}`. Verbatim port of.
 *
 * This is `\maketitle`'s block (R-150, ) **minus the date paragraph** — the
 * source duplicated the code and dropped. Consequences:
 * - `\afficherTitre{}` never emits `<p class="date">`, so a document that uses the preamble's
 *   `\afficherTitre` (`pre:107`, which expands to `\maketitle…`) shows a date in Mode A but
 *   an explicit `\afficherTitre{}` in Mode B does not.
 * - Because there is no date branch, this function needs **no clock** and stays pure. The
 *   `new Date()` in R-150 belongs to whichever module owns `\maketitle`.
 *
 * Faithful details:
 * - `if (titleContent)` / `if (authorContent)` are **truthiness** tests: an empty string
 *   suppresses the element, exactly like `null`.
 * - The title rewrite `\\par\s*` → `<br>` has no word boundary, so a `\paragraphe` in a
 *   title becomes `<br>agraphe`.
 * - The size chain is `if LARGE / else if Large / else if large`, first match wins: an author
 *   containing both `\Large` and `\large` gets `Large-text` and keeps a literal `\large`.
 */
export function buildTitleBlockNoDate(metadata: DocumentMetadata): string {
  const titleContent = metadata.title
  const authorContent = metadata.author

  let titleBlock = ''

  if (titleContent) {
    // \par inside a title becomes a line break (no \b: \paragraphe → <br>agraphe)
    const processedTitle = titleContent.replace(/\\par\s*/g, '<br>')
    
    titleBlock += `<h1 class="title no-toc">${processedTitle}</h1>`
  }

  if (authorContent) {
    // size switch detected on the raw author, first match wins
    let authorClass = 'author'
    let processedAuthor = authorContent

    if (processedAuthor.includes('\\LARGE')) {
      authorClass += ' LARGE-text'
      processedAuthor = processedAuthor.replace(/\\LARGE\s*/g, '')
    } else if (processedAuthor.includes('\\Large')) {
      authorClass += ' Large-text'
      processedAuthor = processedAuthor.replace(/\\Large\s*/g, '')
    } else if (processedAuthor.includes('\\large')) {
      authorClass += ' large-text'
      processedAuthor = processedAuthor.replace(/\\large\s*/g, '')
    }

    
    titleBlock += `<p class="${authorClass}">${processedAuthor}</p>`
  }

  return titleBlock
}

/**
 * `\afficherTitre{}` → the title block (no date). R-174,.
 *
 * **The braces must be present and empty.** `/\\afficherTitre\{\}/g` matches neither a bare
 * `\afficherTitre` nor `\afficherTitre{ }` — both leak verbatim. In Mode A that does not
 * matter, because S7 expands `\afficherTitre` (zero-arg, `pre:107`) before this rule can see
 * it and the trailing `{}` is swept up by the empty-brace cleanup at S14/S20. In Mode B a
 * document written the way the preamble intends — bare `\afficherTitre` — publishes the
 * literal command text.
 *
 * The replacement is a **function**, so `$&`, `$$` and friends inside `\title`/`\author`
 * are inserted literally (unlike {@link applyAfficherEndNotes}).
 */
export function applyAfficherTitre(html: string, metadata: DocumentMetadata): string {
  // R-174,  — note `\{\}`: only the empty-argument spelling matches
  return html.replace(/\\afficherTitre\{\}/g, () => buildTitleBlockNoDate(metadata))
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * R-175 (#34, ) — \afficherSommaire{}
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/** The placeholder S17/S18 later swap for the real `<nav class="table-of-contents">`. */
export const TOC_PLACEHOLDER = '<nav class="toc-placeholder">[Table of Contents]</nav>'

/**
 * `\afficherSommaire{}` → the TOC placeholder. R-175,.
 *
 * Same empty-braces-only constraint as R-174: bare `\afficherSommaire` leaks in Mode B, and
 * `\afficherSommaire{X}` leaks too.
 */
export function applyAfficherSommaire(html: string): string {
  // R-175
  return html.replace(/\\afficherSommaire\{\}/g, TOC_PLACEHOLDER)
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * R-269 (#128, ) — \afficherEndNotes
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * `\afficherEndNotes` → the notes block collected at S8, or `''`. R-269,.
 *
 * In Mode A this rule never runs: `\afficherEndNotes` is expanded away at S7. That was DF-1
 * while the Pandoc branch won and it expanded to the empty string; since 2026-08-29 S1b keeps
 * the XeLaTeX branch, so it expands to a body ending in `\theendnotes` and `notes.ts`'s R-270
 * performs the insertion instead. In Mode B this rule does run — but with `\note` unhandled
 * (R-349) there is usually nothing to inject, so the observable result is still an empty
 * string. Note this is the SECOND implementation of R-269; `index.ts` wires `notes.ts`'s.
 *
 * **NEW DEFECT — reported, not fixed.** The substitution is `String.replace(regexp, string)`
 * with the notes HTML as the *replacement string*, so JS replacement patterns inside a
 * translator's note are interpreted rather than inserted:
 *
 * | in the note | published as                                    |
 * |-------------|-------------------------------------------------|
 * | `$$`        | `$`  — one character silently deleted            |
 * | `$&`        | the literal text `\afficherEndNotes`             |
 * | `` $` ``    | everything in the document *before* the command  |
 * | `$'`        | everything in the document *after* the command   |
 *
 * Confirmed end-to-end against the oracle: `\footnote{Une note avec $$ dedans.}` renders as
 * `Une note avec $ dedans.` Same family as DF-8, same one-line fix (use a replacer function)
 * and `\theendnotes` at  (R-270), which is the *only* injection point that works
 * in production, has the identical defect. Both are kept.
 *
 * `globalFootnotesHtml || ''` is reproduced by the `|| ''`: an empty context value yields an
 * empty replacement, not the string `undefined`.
 */
export function applyAfficherEndNotes(html: string, ctx: ConversionContext): string {
  // R-269,  — string replacement, NOT a replacer function: `$`-patterns are live
  return html.replace(/\\afficherEndNotes/g, ctx.endnotesHtml || '')
}

/* ────────────────────────────────────────────────────────────────────────────────────────
 * Composed slice
 * ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Rules #17–#34 of `processCommands`, in exact source order —  through,
 * i.e. R-158…R-175. This block is **contiguous** in the source: #16 is `\textsuperscript`
 * and #35 is `\addcontentsline`, both generic.
 *
 * The coordinator must call this **between** those two rules. Applying it earlier or later
 * changes output: `\appel{}` must already be `<p class="appel">` before {@link applyCitation}
 * runs, so that a citation opening with a call-word emits the paragraph outside the
 * blockquote.
 *
 * Reads only `ctx.metadata` (for R-174). Does not mutate `ctx`.
 */
/**
 * R-348 — a call word's group braces stop being published as scripture.
 *
 * PREAMBLE LINK: the `\appel*` family (preambuleV3.tex:342, :363, :368, :396, :401). FIXED
 * 2026-08-31; catalogued before that as **A8** in `docs/PREAMBLE-NOT-HONOURED.md`.
 *
 * Five call-word macros wrap their argument in a **bare group** and end it with `\nopagebreak`:
 *
 * ```latex
 * \newcommand{\appelGR}[1]{\par{#1}\nopagebreak\smallskip}
 * \newcommand{\appelCRS}[1]{\par\noindent\hfil{#1}\nopagebreak\medskip}
 * ```
 *
 * `{…}` is a *scoping group* in LaTeX and prints nothing. This converter has no model of a
 * group, so after macro expansion the braces simply survived, and
 * `\appelGR{AU NOM DE DIEU, LE CLÉMENT, LE MISÉRICORDIEUX}` — the opening line of
 * *Le Secret de la civilisation divine* — published **with its braces around the Divine Name**.
 *
 * The rule below keys on the preamble's own idiom, `{…}` immediately followed by
 * `\nopagebreak`, which appears in exactly these five macros and nowhere else in the file.
 * Anywhere else, braces are left alone — this converter leaks many commands verbatim (R-329,
 * R-330, R-338 …) and a general brace strip would turn `\url{x}` into `\urlx`.
 *
 * **It must run before R-241 (slot 13) deletes `\nopagebreak`**, which is the anchor it needs.
 *
 * ⚠️ **`\appelCRLS` is only half fixed by this.** Its group is `{\Large #1}`, so the braces go
 * but R-201 still deletes the `\Large` and leaves a stray space — that is **A14**, a separate
 * defect, deliberately still open. Deferred by the owner along with `\KAquestion` /
 * `\KAreponse`, whose `{#1}` has no `\nopagebreak` to key on and needs its own handle.
 */
export function stripCallWordGroupBraces(html: string): string {
  // R-348. Two guards, and BOTH are load-bearing:
  //
  //   1. The group must FOLLOW an argument-less spacing command. A first draft matched any
  //      `{…}\nopagebreak` and promptly ate the argument braces of `\textbf{X}\nopagebreak`,
  //      turning it into `\textbfX` — it cannot be told lexically whether `{` opens a bare
  //      group or a command's argument, so the preceding command is named explicitly. The
  //      list is exactly what the five call-word bodies put in front of their group.
  //   2. `(?=\\nopagebreak)` — the group must be the one those macros close with.
  //
  // `[^{}]*` — no nested braces, as everywhere else in this converter. A call word containing
  // markup (`\appelGR{Mot \textbf{fort}}`) is NOT matched and keeps its braces. Recorded.
  // The replacement leaves `{}` where the group was, NOT bare text. That empty group is a
  // delimiter, and it matters: `\par{X}` → `\parX` glues the call word onto the command, and
  // R-237's `/\\par\b/` then finds no word boundary and never fires — `\par` was published as
  // literal text in front of the Divine Name. `\par{}X` keeps the boundary, R-237 fires, and
  // S14's `stripEmptyBraces` (R-299) clears the `{}` afterwards. No stray space is introduced,
  // which is what a plain `'$1 $2'` would have cost — see A14 for what that looks like.
  return html.replace(
    /(\\(?:par|bigskip|medskip|smallskip|hfill?|noindent))\{([^{}]*)\}(?=\\nopagebreak)/g,
    '$1{}$2',
  )
}

export function applyDomainCommands(html: string, ctx: ConversionContext): string {
  // PREAMBLE LINK: the whole "Commandes de formats" section of preambuleV3.tex (:208-449) — \ts,
  // \appel*, \citation, \Nverset/\Npar/\Nhidden, \afficherTitre, \afficherSommaire. NOT READ, and
  // not needed on the live path: production concatenates the preamble ahead of the document, so
  // macros.ts has already rewritten each of those macros into generic LaTeX using the preamble's
  // OWN \newcommand bodies before this stage runs. In that sense the preamble is honoured
  // exactly — by expansion, not by these handlers, every one of which is dead in production.
  // /!\ CONDITIONAL, NOT ABSOLUTE. Clear the preamble box and there are no macros to expand; these
  // handlers then fire for real and emit the domain classes .nverset, .appel-*, .text-underbar.
  // Both modes ship. Do not delete anything here on the grounds that Mode A never reaches it, and
  // do not add handlers for the macros listed in DOMAIN_MACROS_WITHOUT_HANDLER above without
  // saying so — that changes what an empty-preamble conversion publishes.
  let result = html
  result = stripCallWordGroupBraces(result) // R-348 — MUST precede R-241 (slot 13)
  result = applyTs(result) // #17  R-158
  result = applyAppelCommands(result) // #18–#28 R-159…R-169
  result = applyCitation(result) // #29  R-170
  result = applyStandaloneVerseNumbers(result) // #30–#32 R-171…R-173
  result = applyAfficherTitre(result, ctx.metadata) // #33  R-174
  result = applyAfficherSommaire(result) // #34  R-175
  return result
}
